import React from "react";
import { useSettings } from "@/context/SettingsContext";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";

export default function Contact() {
  const { settings: s } = useSettings();
  const addr = [s.address_line1, s.address_line2, s.city, s.state, s.pincode, s.country].filter(Boolean).join(", ");
  const wa = (s.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6">
      <div className="mb-10 max-w-2xl">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[hsl(var(--brand-orange))]">Contact</div>
        <h1 className="mt-2 font-display text-4xl font-bold">We'd love to hear from you</h1>
        <p className="mt-3 text-base text-muted-foreground">
          Questions about courses, enrollment, or technical issues? Reach out — we usually reply within one working day.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {s.phone_primary && (
          <Card className="border-border/70">
            <CardContent className="flex gap-4 p-5">
              <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[hsl(var(--brand-navy))]/10 text-[hsl(var(--brand-navy))]"><Phone className="h-5 w-5" /></div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Phone</div>
                <a href={`tel:${s.phone_primary}`} className="font-display text-lg font-bold" data-testid="contact-phone">{s.phone_primary}</a>
                {s.phone_secondary && <div className="text-sm text-muted-foreground"><a href={`tel:${s.phone_secondary}`}>{s.phone_secondary}</a></div>}
              </div>
            </CardContent>
          </Card>
        )}
        {s.whatsapp && (
          <Card className="border-border/70">
            <CardContent className="flex gap-4 p-5">
              <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-700"><MessageCircle className="h-5 w-5" /></div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">WhatsApp</div>
                <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="font-display text-lg font-bold" data-testid="contact-whatsapp">{s.whatsapp}</a>
                <div className="text-xs text-muted-foreground">Tap to chat on WhatsApp</div>
              </div>
            </CardContent>
          </Card>
        )}
        {s.email && (
          <Card className="border-border/70">
            <CardContent className="flex gap-4 p-5">
              <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[hsl(var(--brand-orange))]/15 text-[hsl(var(--brand-orange))]"><Mail className="h-5 w-5" /></div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</div>
                <a href={`mailto:${s.email}`} className="font-display text-lg font-bold" data-testid="contact-email">{s.email}</a>
                {s.support_email && s.support_email !== s.email && <div className="text-sm text-muted-foreground">Support: <a href={`mailto:${s.support_email}`}>{s.support_email}</a></div>}
              </div>
            </CardContent>
          </Card>
        )}
        {s.business_hours && (
          <Card className="border-border/70">
            <CardContent className="flex gap-4 p-5">
              <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-violet-500/15 text-violet-700"><Clock className="h-5 w-5" /></div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Business hours</div>
                <div className="font-display text-lg font-bold">{s.business_hours}</div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {(addr || s.google_maps_url) && (
        <Card className="mt-6 border-border/70">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-rose-500/15 text-rose-700"><MapPin className="h-5 w-5" /></div>
              <div className="flex-1">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Visit us</div>
                {addr && <div className="mt-1 font-display text-lg font-bold" data-testid="contact-address">{addr}</div>}
                {s.google_maps_url && (
                  <a href={s.google_maps_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-sm font-semibold text-[hsl(var(--brand-orange))] hover:underline">
                    Open in Google Maps →
                  </a>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!s.phone_primary && !s.email && !s.whatsapp && (
        <Card className="mt-6 border-dashed border-border bg-secondary/50">
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Your contact details haven't been added yet. An administrator can add them from the Admin → Settings tab.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
